/**
 * Axios-based HTTP client for Laravel /rms-api: JSON CRUD, shared headers, error modal hooks,
 * and blob error-body parsing when responses use `responseType: 'blob'`.
 */
// eslint-disable-next-line
import { injectable } from 'inversify';
import axios, { AxiosHeaders, AxiosRequestConfig, AxiosResponse, HttpStatusCode } from 'axios';
import errorModalStore from '../components/error-modal/error-modal-store';
import { uuid } from './helpers';
import type { IIframeService } from './iframe-service/iframe-service';
import portalContextService from './portal-context-service';

export const CURRENT_PORTAL_ID_HEADER = 'X-Current-Portal-Id';
export const SESSION_PORTAL_ID_HEADER = 'X-Session-Portal-Id';

enum Method {
	GET = 'GET',
	POST = 'POST',
	PATCH = 'PATCH',
	PUT = 'PUT',
	DELETE = 'DELETE'
}

export interface IApiResponse<Type> {
	payload: Type;
}

enum ContentType {
	JSON = 'application/json',
	FORM_DATA = 'multipart/form-data',
}

interface RequestParams {
    silent?: boolean;
}

export interface HttpRequestOptions extends RequestParams {
	/** Overrides default `Accept` (JSON requests default to `application/json`). */
	accept?: string;
	/** Merged into the axios config after method, headers, data, and `withCredentials`. */
	axiosOverrides?: Partial<AxiosRequestConfig>;
	/** When true, resolves with the full `AxiosResponse` instead of `response.data`. */
	returnFullResponse?: boolean;
}

export interface IHttpService {
	get: <T>(path: string, queryParams?: Record<string, any>, options?: RequestParams) => Promise<T>;
	post: <T>(path: string, data?: any) => Promise<T>;
	postFormData: <T>(path: string, formData: FormData, options?: HttpRequestOptions) => Promise<T>;
	patch: <T>(path: string, data?: any) => Promise<T>;
	put: <T>(path: string, data?: any) => Promise<T>;
	remove: <T>(path: string, data?: any) => Promise<T>;
	setCustomHeaders: (headers: Record<string, string>) => void;
	getCustomHeaders: () => Record<string, string>;
	getBaseUrl: () => string | undefined;
}

@injectable()
class HttpService implements IHttpService {
	private baseUrl: string | undefined;
	private withCredentials: boolean;
	private customHeaders: Record<string, string> = {};

	constructor(
		baseURL: string | undefined,
		withCredentials: boolean,
		private iframeService: IIframeService,
		private useErrorModal: boolean = true
	) {
		this.baseUrl = baseURL;
		this.withCredentials = withCredentials;
	}

	get<T>(path: string, queryParams?: Record<string, any>, options?: RequestParams) {
		return this.makeJsonRequest<T>(Method.GET, `${path}${this.stringifyParams(queryParams)}`, null, options);
	};

	post<T>(path: string, data?: any, params?: RequestParams) {
		return this.makeJsonRequest<T>(Method.POST, path, data, params);
	};

	patch<T>(path: string, data?: any, params?: RequestParams) {
		return this.makeJsonRequest<T>(Method.PATCH, path, data, params);
	};

	put<T>(path: string, data?: any, params?: RequestParams) {
		return this.makeJsonRequest<T>(Method.PUT, path, data, params);
	};

	remove<T>(path: string, data?: any, params?: RequestParams) {
		return this.makeJsonRequest<T>(Method.DELETE, path, data, params);
	};

	public getBaseUrl(): string | undefined {
		return this.baseUrl;
	}

	public setCustomHeaders(headers: Record<string, string>): void {
		this.customHeaders = headers;
	}

	public getCustomHeaders(): Record<string, string> {
		return { ...this.customHeaders };
	}

	postFormData<T>(path: string, formData: FormData, options?: HttpRequestOptions) {
		return this.makeRequest<T>(Method.POST, ContentType.FORM_DATA, path, formData, options) as Promise<T>;
	};
	private makeJsonRequest<T>(type: Method, path: string, data?: any, options?: HttpRequestOptions): Promise<T> {
		return this.makeRequest<T>(type, ContentType.JSON, path, data, options) as Promise<T>;
	};

	private async makeRequest<T>(
		type: Method,
		contentType: ContentType,
		path: string,
		data?: any,
		options?: HttpRequestOptions
	): Promise<T | AxiosResponse> {
		const headers: Record<string, string> = {
			Accept: options?.accept ?? ContentType.JSON,
			'Content-Type': contentType,
			'X-Request-Id': generateContextId(),
			...this.customHeaders
		};

		this.attachPortalHeaders(headers);
		const body = contentType === ContentType.JSON ? data && JSON.stringify(data) : data;

		try {
			const config: AxiosRequestConfig = {
				method: type,
				headers,
				data: body,
				...options?.axiosOverrides,
			};
			if (this.withCredentials) {
				config.withCredentials = true;
			}
			const response = await axios(`${this.baseUrl}${path}`, config);
			this.syncPortalFromResponse(response.headers);

			if (options?.returnFullResponse) {
				return response;
			}
			return response.data as T;
		}
		catch (error: any) {
			if (error.response) {
				// Request made and server responded
				console.error('response', error.response);

                const responseData = error.response.data;

                if (error.response.status === HttpStatusCode.Unauthorized && !responseData?.external_api_error) {
                    portalContextService.clearSessionPortalId();
                    this.iframeService.refreshParentFrame();

                    return new Promise(() => {});
                }

				if (this.useErrorModal) {
					const validationErrors = responseData?.errors;
					if (validationErrors) {
						for (let i in validationErrors) {
							errorModalStore.add({
								title: validationErrors[i][0] ?? 'Server Error',
								url: formatEndpoint(error),
								code: responseData?.code || error.response.status
							});
						}
					} else {
						errorModalStore.add({
							...responseData,
							title: error.response.statusText ?? 'Server Error',
							url: formatEndpoint(error),
							code: responseData?.code || error.response.status
						});
					}
				}
			} else if (error.request) {
				// The request was made but no response was received
				console.error('request', error.request);

				if (this.useErrorModal) {
					errorModalStore.add({
						title: error.message,
						url: formatEndpoint(error),
					});
				}
			} else {
				// Something happened in setting up the request that triggered an Error
				console.error(error);

				if (this.useErrorModal) {
					errorModalStore.add({
						title: error.message,
						url: formatEndpoint(error),
					});
				}
			}

			throw error;
		}
	};

	private attachPortalHeaders(headers: Record<string, string>): void {
		const tabPortalId = portalContextService.tabPortalId;
		if (tabPortalId) {
			headers[CURRENT_PORTAL_ID_HEADER] = String(tabPortalId);
		}
		const sessionPortalId = portalContextService.getSessionPortalId();
		if (sessionPortalId) {
			headers[SESSION_PORTAL_ID_HEADER] = String(sessionPortalId);
		}
	}

	private syncPortalFromResponse(headers: AxiosResponse['headers'] | undefined): void {
		let echoed;
		if (headers instanceof AxiosHeaders) {
			echoed = Number(headers.get(CURRENT_PORTAL_ID_HEADER));
		} else {
			echoed = Number(headers?.[CURRENT_PORTAL_ID_HEADER]);
		}
		if (echoed > 0) {
			portalContextService.setTabPortalId(echoed);
		}
	}

	private stringifyParams(params: any) {
		if (!params) return '';
		var keyValPairs = [];
		for (var p in params)
			keyValPairs.push(encodeURIComponent(p) + "=" + encodeURIComponent(params[p]));
		return keyValPairs.length ? `?${keyValPairs.join("&")}` : '';
	}

}

function formatEndpoint(error: any) {
	return [
		(error.config?.method ?? '').toUpperCase(),
		error.config?.url,
	].filter(s => !!s).join(' ');
}

export function generateContextId() {
	let generatedUuid = uuid();
	generatedUuid = generatedUuid.replace(/-/g, ''); // Remove dashes from UUID
	// Generate a unique context ID for the request
	return `RMS_${generatedUuid}_${Date.now()}`;
}

export default HttpService;
